import { useState, useEffect } from "react";
import {
  fetchTournamentsAsync,
  fetchTournamentLessonsAsync,
  DEFAULT_TOURNAMENT_DESCRIPTION,
  type TournamentFormState,
} from "@/api/tournaments";

export function useTournamentData(
  tournamentId: string | undefined,
  isEditMode: boolean
): {
  tournament: TournamentFormState;
  setTournament: React.Dispatch<React.SetStateAction<TournamentFormState>>;
  isLoading: boolean;
  isInitialized: boolean;
  isOngoing: boolean;
} {
  const [tournament, setTournament] = useState<TournamentFormState>({
    basicInfo: {
      name: "",
      description: isEditMode ? "" : DEFAULT_TOURNAMENT_DESCRIPTION,
    },
    audience: {
      users: [],
      isGlobal: false,
    },
    content: {
      selected: [],
    },
    settings: {
      startDate: null,
      endDate: null,
      contactPersons: "",
      prizes: "",
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(!isEditMode);
  const [isOngoing, setIsOngoing] = useState(false);

  useEffect(() => {
    if (!isEditMode || isInitialized) return;

    let cancelled = false;

    const hydrateTournamentData = async () => {
      setIsLoading(true);
      try {
        const tournaments = await fetchTournamentsAsync();
        const existingTournament = tournaments.find(t => String(t.id) === String(tournamentId));

        if (!existingTournament) {
          console.error("Tournament not found");
          setIsInitialized(true);
          return;
        }

        const allLessons = await fetchTournamentLessonsAsync({});

        const hydratedSelectedLessons = (existingTournament.content ?? [])
          .map(c =>
            allLessons.find(lesson => {
              const typeMatches = lesson.type.toLowerCase() === c.type.toLowerCase();
              if (!typeMatches) return false;
              const directMatch = String(lesson.latestLessonId) === String(c.id);
              const groupMatch = lesson.allLessonsIds?.map(String).includes(String(c.id)) ?? false;
              return directMatch || groupMatch;
            })
          )
          .filter((l): l is NonNullable<typeof l> => l != null);

        if (cancelled) return;

        setTournament({
          basicInfo: {
            name: existingTournament.name ?? "",
            description: existingTournament.summary ?? "",
          },
          audience: {
            users: existingTournament.audience ?? [],
            isGlobal: existingTournament.isGlobal ?? false,
          },
          content: {
            selected: hydratedSelectedLessons,
          },
          settings: {
            startDate: existingTournament.startDate ?? null,
            endDate: existingTournament.endDate ?? null,
            contactPersons: existingTournament.contactInfo ?? "",
            prizes: existingTournament.prizes ?? "",
          },
        });
        setIsOngoing(!!existingTournament.ongoing);
        setIsInitialized(true);
      } catch (error) {
        console.error("Error fetching tournament data:", error);
        if (!cancelled) setIsInitialized(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    hydrateTournamentData();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, tournamentId, isInitialized]);

  return { tournament, setTournament, isLoading, isInitialized, isOngoing };
}
